import React, { useState, useEffect } from 'react';
import { Search, Filter, Pause, Play, Archive, Trash2, Shield, MoreHorizontal, Radio, BrainCircuit, BarChart3, Users, Lock, Unlock, LogOut, Star, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { canManagePlatform } from '../utils/permissions';
import ConfirmModal from '../components/ConfirmModal';

const AdminActivities = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });

  useEffect(() => {
    if (user && !canManagePlatform(user)) {
      navigate('/');
      return;
    }
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const [rooms, discussions, polls] = await Promise.all([
        api.get('/public/rooms'),
        api.get('/public/discussions'),
        api.get('/public/polls')
      ]);
      
      const allActivities = [
        ...(Array.isArray(rooms) ? rooms : []).map(r => ({ ...r, type: 'Session', host: r.owner?.name || 'Admin' })),
        ...(Array.isArray(discussions) ? discussions : []).map(d => ({ ...d, type: 'Discussion', host: d.author?.name || 'User' })),
        ...(Array.isArray(polls) ? polls : []).map(p => ({ ...p, type: 'Poll', host: 'System' }))
      ];
      
      setActivities(allActivities);
    } catch (err) {
      console.error('Failed to fetch activities', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.put(`/rooms/${id}`, { status: newStatus });
      setActivities(prev => Array.isArray(prev) ? prev.map(a => a.id === id ? { ...a, status: newStatus } : a) : []);
      setConfirmConfig({ isOpen: false });
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const toggleFeatured = async (id, currentFeatured) => {
    try {
      await api.put(`/rooms/${id}`, { is_featured: !currentFeatured });
      setActivities(prev => Array.isArray(prev) ? prev.map(a => a.id === id ? { ...a, is_featured: !currentFeatured } : a) : []);
    } catch (err) {
      console.error('Failed to toggle featured status', err);
    }
  };

  const deleteActivity = async (id) => {
    try {
      await api.delete(`/rooms/${id}`);
      setActivities(prev => Array.isArray(prev) ? prev.filter(a => a.id !== id) : []);
      setConfirmConfig({ isOpen: false });
    } catch (err) {
      console.error('Failed to delete activity', err);
    }
  };

  const openConfirm = (id, type, action) => {
    if (action === 'delete') {
      setConfirmConfig({
        isOpen: true,
        title: 'Delete Activity?',
        description: 'Are you sure you want to delete this activity? This action cannot be undone.',
        confirmText: 'Yes, Delete',
        onConfirm: () => deleteActivity(id),
        type: 'danger'
      });
    } else if (action === 'stop') {
      setConfirmConfig({
        isOpen: true,
        title: 'Force Stop Session?',
        description: 'This will immediately end the session and mark it as archived.',
        confirmText: 'Stop Session',
        onConfirm: () => updateStatus(id, 'Archived'),
        type: 'warning'
      });
    }
  };

  const getIcon = (type) => {
    switch(type) {
      case 'Q&A': 
      case 'Discussion': return MessageSquare;
      case 'Brainstorm': return BrainCircuit;
      case 'Poll': return BarChart3;
      default: return Radio;
    }
  };

  const filteredActivities = (Array.isArray(activities) ? activities : []).filter(a => 
    (a.title || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
    (a.host || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (a.type || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Activity Management</h1>
          <p className="text-gray-500 font-medium mt-1">Monitor and control all live sessions, brainstorms, and polls.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Search activities..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white border border-gray-100 rounded-xl text-sm focus:ring-2 focus:ring-brand-indigo/10 outline-none w-64"
            />
          </div>
          <button className="p-2 bg-white border border-gray-100 rounded-xl text-gray-600 hover:bg-gray-50 transition-all">
            <Filter size={20} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] border border-gray-100 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-50">
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Activity</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Type</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Status</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Participants</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredActivities.map((activity) => (
              <tr key={activity.id} className="hover:bg-gray-50/30 transition-colors group">
                <td className="px-8 py-5">
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{activity.title}</p>
                    <p className="text-xs text-gray-500 font-medium">Host: {activity.host}</p>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <span className={cn(
                    "text-xxs font-bold px-2 py-1 rounded-lg uppercase tracking-widest",
                    activity.type === 'Q&A' ? "bg-purple-50 text-purple-600" :
                    activity.type === 'Brainstorm' ? "bg-green-50 text-green-600" :
                    activity.type === 'Poll' ? "bg-brand-indigo/10 text-brand-indigo" :
                    "bg-gray-50 text-gray-500"
                  )}>
                    {activity.type}
                  </span>
                </td>
                <td className="px-8 py-5">
                  <span className={cn(
                    "flex items-center gap-1.5 text-xs font-bold",
                    activity.status === 'Live' ? "text-red-600" : 
                    activity.status === 'Paused' ? "text-orange-600" : 
                    activity.status === 'Locked' ? "text-amber-600" : "text-gray-500"
                  )}>
                    <span className={cn("w-1.5 h-1.5 rounded-full", 
                      activity.status === 'Live' ? "bg-red-500 animate-pulse" : 
                      activity.status === 'Paused' ? "bg-orange-500" : 
                      activity.status === 'Locked' ? "bg-amber-500" : "bg-gray-400"
                    )} />
                    {activity.status}
                  </span>
                </td>
                <td className="px-8 py-5 text-sm font-bold text-gray-900">
                  <div className="flex items-center gap-1.5">
                    <Users size={14} className="text-gray-400" />
                    {activity.participants}
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => toggleFeatured(activity.id, activity.is_featured)}
                      className={cn(
                        "p-2 rounded-xl transition-all",
                        activity.is_featured ? "bg-yellow-50 text-yellow-500" : "bg-gray-50 text-gray-400 hover:bg-yellow-50 hover:text-yellow-500"
                      )} 
                      title={activity.is_featured ? "Unfeature" : "Feature on Homepage"}
                    >
                      <Star size={18} fill={activity.is_featured ? "currentColor" : "none"} />
                    </button>
                    {activity.status === 'Live' && (
                      <button 
                        onClick={() => updateStatus(activity.id, 'Paused')}
                        className="p-2 bg-orange-50 text-orange-600 rounded-xl hover:bg-orange-100 transition-all" 
                        title="Pause Activity"
                      >
                        <Pause size={18} />
                      </button>
                    )}
                    {activity.status === 'Paused' && (
                      <button 
                        onClick={() => updateStatus(activity.id, 'Live')}
                        className="p-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-all" 
                        title="Resume Activity"
                      >
                        <Play size={18} />
                      </button>
                    )}
                    {activity.status !== 'Locked' ? (
                      <button 
                        onClick={() => updateStatus(activity.id, 'Locked')}
                        className="p-2 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-100 transition-all" 
                        title="Lock (Read-only)"
                      >
                        <Lock size={18} />
                      </button>
                    ) : (
                      <button 
                        onClick={() => updateStatus(activity.id, 'Live')}
                        className="p-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-all" 
                        title="Unlock Activity"
                      >
                        <Unlock size={18} />
                      </button>
                    )}
                    <button 
                      onClick={() => updateStatus(activity.id, 'Archived')}
                      className="p-2 bg-brand-indigo/10 text-brand-indigo rounded-xl hover:bg-brand-indigo/20 transition-all" 
                      title="Deactivate Activity"
                    >
                      <Archive size={18} />
                    </button>
                    <button 
                      onClick={() => openConfirm(activity.id, activity.type, 'stop')}
                      className="p-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-all" 
                      title="Force Stop"
                    >
                      <LogOut size={18} />
                    </button>
                    <button 
                      onClick={() => openConfirm(activity.id, activity.type, 'delete')}
                      className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all" 
                      title="Delete Activity"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <ConfirmModal 
        {...confirmConfig}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
      />
    </div>
  );
};

export default AdminActivities;
